import { RemoteKey } from 'thingrtc-peer';
import { IDisposable, Terminal } from 'xterm';
import { FitAddon } from '@xterm/addon-fit';
import { addConnection, Connection, deleteConnection, loadConnections } from './connections';
import { connect, loadLocalKeyPair, PeerEventListeners } from './peer';

const dialog = document.getElementById('dialog') as HTMLDivElement;
const localPublicKey = document.getElementById('localPublicKey') as HTMLInputElement;
const connectionsList = document.getElementById('connections') as HTMLDivElement;
const addConnectionButton = document.getElementById('addConnection') as HTMLButtonElement;
const addConnectionDialog = document.getElementById('addConnectionDialog') as HTMLDivElement;
const saveConnectionButton = document.getElementById('saveConnection') as HTMLButtonElement;
const cancelConnectionButton = document.getElementById('cancelConnection') as HTMLButtonElement;
const terminal = document.getElementById('terminal') as HTMLDivElement;
const peerPublicKeyField = document.getElementById('peerPublicKey') as HTMLInputElement;
const nicknameField = document.getElementById('nickname') as HTMLInputElement;
const usernameField = document.getElementById('username') as HTMLInputElement;
const passwordField = document.getElementById('password') as HTMLInputElement;
const loadingDialog = document.getElementById('loadingDialog') as HTMLDivElement;
const loadingMessage = document.getElementById('loadingMessage') as HTMLParagraphElement;
const toast = document.getElementById('toast') as HTMLDivElement;
const ctrlButton = document.getElementById('ctrlButton') as HTMLButtonElement;

const term = new Terminal({fontSize: 12});
const fitAddon = new FitAddon();
term.loadAddon(fitAddon);
term.open(terminal);

let disposables: IDisposable[] = [];
let peerWriter: ((data: string) => void)|null = null;
let ctrlEnabled = false;

// Only fit once the page has loaded and layout has been calculated.
window.addEventListener('load', () => {
    fitAddon.fit();
    setup();
});

// Resize the terminal on window resize.
window.addEventListener('resize', () => {
    fitAddon.fit();
});

async function setup() {
    const localKeyPair = await loadLocalKeyPair();
    localPublicKey.value = localKeyPair.publicKeySpki;

    renderConnections();
    setupHelperKeys();
}

function renderConnections() {
    const connections = loadConnections();
    if (connections.length > 0) {
        connectionsList.innerHTML = '';
        const list = document.createElement('ul');
        connectionsList.appendChild(list);

        for (const c of connections) {
            const item = document.createElement('li');
            const itemLabel = document.createElement('span');
            itemLabel.classList.add('label');
            itemLabel.innerText = c.nickname;
            item.appendChild(itemLabel);

            const deleteButton = document.createElement('button');
            deleteButton.innerText = 'Delete';
            deleteButton.classList.add('basic-button');
            deleteButton.addEventListener('click', event => {
                deleteConnection(c);
                renderConnections();
                // Do not propagate click to item itself.
                event.stopPropagation();
            });
            item.appendChild(deleteButton);

            item.addEventListener('click', async () => await handleConnect(c));

            list.appendChild(item);
        }
    } else {
        connectionsList.innerHTML = 'No connections configured.';
    }
}

async function handleConnect(connection: Connection) {
    // Clear terminal listeners set on the last connection:
    disposables.forEach(d => d.dispose());
    disposables = [];

    const listeners: PeerEventListeners = {
        onConnecting: () => {
            showLoadingDialog('Waiting for peer...');
        },
        onPeerConnected: () => {
            showLoadingDialog('Peer connected. Waiting for SSH...');
        },
        onShellConnected: () => {
            hideLoadingDialog();
            dialog.classList.toggle('hidden', true);
            term.focus();
        },
        onWriteToTerminal: (buf) => {
            term.write(buf);
        },
        onClose: (reason) => {
            term.clear();
            // Hide the keyboard when the connection closes:
            term.blur();
            hideLoadingDialog();
            dialog.classList.toggle('hidden', false);
            showToast(reason);
        }
    };

    const peerConnection = await connect(connection, listeners, term.rows, term.cols);
    peerWriter = peerConnection.insertData;

    // When the terminal produces input, apply any transformations on this, then
    // send over SSH:
    disposables.push(term.onData(str => {
        peerConnection.insertData(transformTermData(str));
    }));

    // Send the terminal's new dimensions over SSH when it resizes:
    disposables.push(term.onResize(dims => {
        peerConnection.resize(dims.rows, dims.cols);
        console.log(`Resize: ${dims.rows} ${dims.cols}`);
    }));
}

function closeAddConnectionDialog() {
    addConnectionDialog.classList.toggle('hidden', true);
}

function showLoadingDialog(message: string) {
    loadingMessage.innerText = message;
    loadingDialog.classList.toggle('hidden', false);
}

function hideLoadingDialog() {
    loadingMessage.innerText = '';
    loadingDialog.classList.toggle('hidden', true);
}

function showToast(message: string) {
    toast.innerText = message;
    toast.classList.add('show');
    setTimeout(() => {
        toast.classList.remove('show');
    }, 3000);
}

function setupHelperKeys() {
    const helperKeyMapping = {
        'tabButton': '\t',
        'downButton': '\x1b[B',
        'upButton': '\x1b[A',
        'leftButton': '\x1b[D',
        'rightButton': '\x1b[C',
        'homeButton': '\x1b[H',
        'endButton': '\x1b[F',
        'escButton': '\x1b',
    };

    for (const [elementId, char] of Object.entries(helperKeyMapping)) {
        const element = document.getElementById(elementId) as HTMLButtonElement;
        
        let intervalHandle: number|undefined;
        element.addEventListener('mousedown', event => {
            // Prevent default, to keep focus in the terminal after pressing the button.
            event.preventDefault();
            peerWriter?.(char);
            intervalHandle = setInterval(() => peerWriter?.(char), 200);
        });
        element.addEventListener('mouseup', () => {
            clearInterval(intervalHandle);
            intervalHandle = undefined;
        });
    }
}

function transformTermData(data: string): string {
    if (ctrlEnabled) {
        // Toggle the CTRL key off after we've processed it once:
        toggleCtrl();
        return applyCtrlModifier(data);
    } else {
        return data;
    }
}

function applyCtrlModifier(char: string) {
    // Handle standard alphabetical characters (a-z, A-Z)
    // A=65. Subtracting 64 gives 1 (\x01, which is CTRL+A)
    if (/^[a-zA-Z]$/.test(char)) {
        return String.fromCharCode(char.toUpperCase().charCodeAt(0) - 64);
    }
    
    // Handle special terminal control symbols
    const specialKeys: {[char: string]: number} = {
        '@': 0,   // \x00
        '[': 27,  // \x1b (Escape)
        '\\': 28, // \x1c
        ']': 29,  // \x1d
        '^': 30,  // \x1e
        '_': 31   // \x1f
    };

    if (specialKeys[char] !== undefined) {
        return String.fromCharCode(specialKeys[char]);
    }

    // If it's not a standard modifiable character, return it unchanged
    return char;
}

function toggleCtrl() {
    ctrlEnabled = !ctrlEnabled;
    ctrlButton.classList.toggle('pressed', ctrlEnabled);
}

addConnectionButton.addEventListener('click', () => {
    addConnectionDialog.classList.toggle('hidden', false);
    // Clear the form on close:
    peerPublicKeyField.value = '';
    nicknameField.value = '';
    usernameField.value = '';
    passwordField.value = '';
});

saveConnectionButton.addEventListener('click', async () => {
    // Try validating the provided public key:
    try {
        const publicKey = peerPublicKeyField.value;
        await RemoteKey.createRemoteKey(publicKey);
        try {
            addConnection({
                nickname: nicknameField.value,
                username: usernameField.value,
                password: passwordField.value,
                publicKey: publicKey,
            });
            renderConnections();
            closeAddConnectionDialog();
        } catch(e) {
            alert(e);
        }
    } catch (err) {
        alert('Invalid public key!');
    }
});

cancelConnectionButton.addEventListener('click', () => {
    closeAddConnectionDialog();
});

// The CTRL button is handled separately from other keys, by toggling its state:
ctrlButton.addEventListener('mousedown', event => {
    event.preventDefault();
    toggleCtrl();
});
